/**
 * Geometry for the orbital category carousel: an elliptical podium ("ring")
 * with tiles arranged on a cylinder that is tilted towards the viewer.
 */

/** Each item appears this many times around the ring so the loop feels full. */
const SLOTS_PER_ITEM = 2;
/** cos(angle) below which a tile is fully transparent (back of the ring). */
const TILE_FADE_START = 0.1;
const TILE_FADE_RANGE = 0.32;

export interface RingLayout {
    rx: number;
    ry: number;
    wall: number;
    pad: number;
    height: number;
    deckPlaneFromBottom: number;
}

export interface TileLayout {
    radius: number;
    flatten: number;
    tileW: number;
    tileH: number;
    itemAngle: number;
    slotCount: number;
    tilt: number;
}

export interface TilePose {
    rotateY: number;
    opacity: number;
    zIndex: number;
}

/** Positive modulo. */
export function wrapIndex(value: number, count: number): number {
    return count <= 0 ? 0 : ((value % count) + count) % count;
}

/** Signed distance of a slot from the current position, in the range (-count/2, count/2]. */
export function slotOffset(slot: number, position: number, count: number): number {
    if (count <= 0) {
        return 0;
    }

    const offset = (((slot - position) % count) + count) % count;

    return offset > count / 2 ? offset - count : offset;
}

/** Shortest signed step count to go from index `from` to index `to`. */
export function shortestStep(from: number, to: number, count: number): number {
    if (count <= 0) {
        return 0;
    }

    const delta = wrapIndex(to, count) - wrapIndex(from, count);

    if (delta > count / 2) {
        return delta - count;
    }

    return delta < -count / 2 ? delta + count : delta;
}

export function ringLayout(containerWidth: number, radius: number, flatten: number): RingLayout {
    const rx = Math.min(radius, (containerWidth > 0 ? containerWidth : 960) * 0.48);
    const ry = rx * flatten;
    const wall = Math.max(64, rx * 0.25);
    const pad = Math.max(20, wall * 0.22);

    return {
        rx,
        ry,
        wall,
        pad,
        height: wall + ry * 2 + pad * 2,
        deckPlaneFromBottom: ry + wall + pad,
    };
}

export function tileLayout(radius: number, flatten: number, wall: number, itemCount: number): TileLayout {
    const slotCount = Math.max(itemCount, 3) * SLOTS_PER_ITEM;
    const itemAngle = (Math.PI * 2) / slotCount;
    const tiltRad = Math.asin(Math.min(0.95, flatten));

    return {
        radius,
        flatten,
        tileW: 2 * radius * Math.tan(itemAngle / 2),
        tileH: wall / Math.cos(tiltRad),
        itemAngle,
        slotCount,
        tilt: -((tiltRad * 180) / Math.PI),
    };
}

export function tilePose(offset: number, layout: TileLayout): TilePose {
    const angle = offset * layout.itemAngle;
    const depth = Math.cos(angle);

    return {
        rotateY: (angle * 180) / Math.PI,
        opacity: Math.min(1, Math.max(0, (depth - TILE_FADE_START) / TILE_FADE_RANGE)),
        zIndex: Math.round(depth * 100) + 120,
    };
}
